import { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormShell, FieldRow, ChipGroup, submitForm, SubmitButton, DoneScreen } from "./FormShell";
import DateInput from "./DateInput";
import { ICON_MAP } from "@/pages/MenuEditor";
import { ForkKnife } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const p = (k, d = "") => new URLSearchParams(window.location.search).get(k) || d;
const todayIso = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};
const isoPlusDays = (days) => {
  const t = new Date();
  t.setDate(t.getDate() + days);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};
const inp = "h-11 rounded-lg border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200";

export default function PublicBreakfast() {
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [d, setD] = useState({
    name: p("name"), zimmer: p("room", ""), datum: todayIso(), uhrzeit: "07:30",
    wuensche: "",
  });
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));

  // Katalog aus Backend laden (live)
  useEffect(() => {
    axios.get(`${API}/api/public/menu-catalog`).then(r => {
      const bf = r.data?.breakfast || { subtitle: "", categories: [] };
      setCatalog(bf);
      // Initialisiere leere Arrays für jede Kategorie
      setD(prev => {
        const next = { ...prev };
        (bf.categories || []).forEach(c => { if (!(c.key in next)) next[c.key] = []; });
        return next;
      });
    }).catch(() => setCatalog({ subtitle: "", categories: [] }));
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!d.datum || d.datum < todayIso() || d.datum > isoPlusDays(365)) {
      alert("Bitte ein gültiges Datum zwischen heute und in einem Jahr wählen."); return;
    }
    submitForm({
      form_type: "breakfast", data: d,
      guest_name: d.name, room_hint: d.zimmer,
      setSaving, onDone: () => setDone(true),
    });
  };

  if (done) return <DoneScreen label="Ihr Frühstück ist bestellt – wir servieren zur gewünschten Uhrzeit." />;
  if (!catalog) return <FormShell title="Frühstücks-Bestellliste zum Ankreuzen"><div className="text-sm text-slate-500">Lade Speisekarte…</div></FormShell>;

  return (
    <FormShell title="Frühstücks-Bestellliste zum Ankreuzen" subtitle={catalog.subtitle}>
      <form onSubmit={submit}>
        <FieldRow label="Name" required>
          <Input value={d.name} onChange={(e) => set("name", e.target.value)} required className={inp} data-testid="bf-name" />
        </FieldRow>
        <FieldRow label="Zimmernummer">
          <Input value={d.zimmer} onChange={(e) => set("zimmer", e.target.value)} placeholder="z. B. Doppelzimmer (optional)" className={inp} data-testid="bf-room" />
        </FieldRow>
        <FieldRow label="Datum" required>
          <DateInput value={d.datum} onChange={(v) => set("datum", v)} required min="today" max={isoPlusDays(365)} testid="bf-datum" />
        </FieldRow>
        <FieldRow label="Uhrzeit" required>
          <Input type="time" value={d.uhrzeit} onChange={(e) => set("uhrzeit", e.target.value)} required className={inp} data-testid="bf-uhrzeit" />
        </FieldRow>

        <div className="mt-8 pt-6 border-t border-slate-100 space-y-8">
          {(catalog.categories || []).map((cat) => (
            <ChipGroup
              key={cat.key}
              icon={ICON_MAP[cat.icon] || ForkKnife}
              label={cat.label}
              options={(cat.items || []).map(i => i.name)}
              prices={Object.fromEntries((cat.items || []).map(i => [i.name, i.price]))}
              value={d[cat.key] || []}
              onChange={(v) => set(cat.key, v)}
              testid={`bf-${cat.key}`}
            />
          ))}
        </div>

        <div className="mt-8 pt-6 border-t border-slate-100">
          <FieldRow label="Besondere Wünsche">
            <Textarea value={d.wuensche} onChange={(e) => set("wuensche", e.target.value)}
                      className="rounded-lg border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200"
                      rows={2} data-testid="bf-wuensche" />
          </FieldRow>
        </div>

        <SubmitButton saving={saving} label="Bestellung absenden" />
      </form>
    </FormShell>
  );
}
